import type {
  IntegrationDataModelSyncSettings,
  UpdateDataModelSyncSettings,
} from '@commenda/api';
import { RootFiWorkspaceSyncFrequencyV4 } from '@commenda/types';

/** Largest interval RootFi accepts per selectable unit; anything else configured in RootFi surfaces read-only. */
export const ROOTFI_SYNC_INTERVAL_LIMITS = {
  [RootFiWorkspaceSyncFrequencyV4.HOURLY]: 24,
  [RootFiWorkspaceSyncFrequencyV4.DAILY]: 31,
  [RootFiWorkspaceSyncFrequencyV4.WEEKLY]: 4,
  [RootFiWorkspaceSyncFrequencyV4.MONTHLY]: 12,
} as const;

export type RootFiSupportedSyncFrequency =
  keyof typeof ROOTFI_SYNC_INTERVAL_LIMITS;

export const ROOTFI_SUPPORTED_SYNC_FREQUENCIES = Object.keys(
  ROOTFI_SYNC_INTERVAL_LIMITS
) as RootFiSupportedSyncFrequency[];

const ROOTFI_SYNC_INTERVAL_OPTIONS: Record<
  RootFiSupportedSyncFrequency,
  number[]
> = {
  [RootFiWorkspaceSyncFrequencyV4.HOURLY]: [1, 2, 4, 6, 8, 12, 24],
  [RootFiWorkspaceSyncFrequencyV4.DAILY]: [1, 2, 3, 7, 14, 30],
  [RootFiWorkspaceSyncFrequencyV4.WEEKLY]: [1, 2, 3, 4],
  [RootFiWorkspaceSyncFrequencyV4.MONTHLY]: [1, 2, 3, 6, 12],
};

/** Sentinel value shown when the underlying data models disagree. */
export const MIXED_SYNC_VALUE = 'mixed';

const SYNC_FREQUENCY_LABELS: Record<RootFiWorkspaceSyncFrequencyV4, string> = {
  [RootFiWorkspaceSyncFrequencyV4.HOURLY]: 'Hourly',
  [RootFiWorkspaceSyncFrequencyV4.DAILY]: 'Daily',
  [RootFiWorkspaceSyncFrequencyV4.WEEKLY]: 'Weekly',
  [RootFiWorkspaceSyncFrequencyV4.MONTHLY]: 'Monthly',
  [RootFiWorkspaceSyncFrequencyV4.YEARLY]: 'Yearly',
  [RootFiWorkspaceSyncFrequencyV4.NEVER]: 'Never',
};

/** Singular unit used with an interval, e.g. "Every 2 hours". */
const SYNC_FREQUENCY_UNIT_LABELS: Record<
  RootFiWorkspaceSyncFrequencyV4,
  string
> = {
  [RootFiWorkspaceSyncFrequencyV4.HOURLY]: 'hour',
  [RootFiWorkspaceSyncFrequencyV4.DAILY]: 'day',
  [RootFiWorkspaceSyncFrequencyV4.WEEKLY]: 'week',
  [RootFiWorkspaceSyncFrequencyV4.MONTHLY]: 'month',
  [RootFiWorkspaceSyncFrequencyV4.YEARLY]: 'year',
  [RootFiWorkspaceSyncFrequencyV4.NEVER]: 'never',
};

export interface RootFiGlobalSyncSettingsPatch {
  frequency?: RootFiSupportedSyncFrequency;
  interval?: number;
  syncFrom?: string | null;
}

export class RootFiDataModelSyncHelper {
  static isSupportedFrequency(
    value: string
  ): value is RootFiSupportedSyncFrequency {
    return (ROOTFI_SUPPORTED_SYNC_FREQUENCIES as readonly string[]).includes(
      value
    );
  }

  static getIntervalLimit(frequency: RootFiSupportedSyncFrequency): number {
    return ROOTFI_SYNC_INTERVAL_LIMITS[frequency];
  }

  static getIntervalOptions(frequency: RootFiSupportedSyncFrequency): number[] {
    return ROOTFI_SYNC_INTERVAL_OPTIONS[frequency];
  }

  static getFrequencyLabel(
    frequency: RootFiWorkspaceSyncFrequencyV4,
    interval = 1
  ): string {
    if (interval === 1 || frequency === RootFiWorkspaceSyncFrequencyV4.NEVER) {
      return SYNC_FREQUENCY_LABELS[frequency] ?? frequency;
    }
    return `Every ${interval} ${SYNC_FREQUENCY_UNIT_LABELS[frequency] ?? frequency}s`;
  }

  static getFrequencyUnitLabel(
    frequency: RootFiWorkspaceSyncFrequencyV4
  ): string {
    const unit = SYNC_FREQUENCY_UNIT_LABELS[frequency] ?? frequency;
    return unit.charAt(0).toUpperCase() + unit.slice(1);
  }

  /** Frequency shared by every data model, or `MIXED_SYNC_VALUE` when they disagree. */
  static getGlobalFrequency(
    settings: IntegrationDataModelSyncSettings[]
  ): RootFiWorkspaceSyncFrequencyV4 | typeof MIXED_SYNC_VALUE {
    const first = settings[0];
    if (!first) return MIXED_SYNC_VALUE;
    return settings.every((item) => item.frequency === first.frequency)
      ? first.frequency
      : MIXED_SYNC_VALUE;
  }

  /** Interval shared by every data model, or `MIXED_SYNC_VALUE` when they disagree. */
  static getGlobalInterval(
    settings: IntegrationDataModelSyncSettings[]
  ): number | typeof MIXED_SYNC_VALUE {
    const first = settings[0];
    if (!first) return MIXED_SYNC_VALUE;
    return settings.every((item) => item.interval === first.interval)
      ? first.interval
      : MIXED_SYNC_VALUE;
  }

  /** Sync-from date shared by every data model; `null` is all history, `MIXED_SYNC_VALUE` when they disagree. */
  static getGlobalSyncFrom(
    settings: IntegrationDataModelSyncSettings[]
  ): string | null | typeof MIXED_SYNC_VALUE {
    const first = settings[0];
    if (!first) return null;
    return settings.every((item) => item.syncFrom === first.syncFrom)
      ? first.syncFrom
      : MIXED_SYNC_VALUE;
  }

  /** Applies the given fields to every model; omitted fields are left untouched. */
  static applyGlobalSettings(
    settings: IntegrationDataModelSyncSettings[],
    patch: RootFiGlobalSyncSettingsPatch
  ): IntegrationDataModelSyncSettings[] {
    return settings.map((item) => ({
      ...item,
      frequency: patch.frequency ?? item.frequency,
      interval: patch.interval ?? item.interval,
      syncFrom: patch.syncFrom !== undefined ? patch.syncFrom : item.syncFrom,
    }));
  }

  /** Only the models whose cadence or sync-from differs from what is saved. */
  static diffForUpdate(
    saved: IntegrationDataModelSyncSettings[],
    draft: IntegrationDataModelSyncSettings[]
  ): UpdateDataModelSyncSettings[] {
    return draft.flatMap((item) => {
      const current = saved.find((s) => s.dataModel === item.dataModel);
      if (!current) return [];
      const frequencyChanged =
        current.frequency !== item.frequency ||
        current.interval !== item.interval;
      const syncFromChanged = current.syncFrom !== item.syncFrom;
      if (!frequencyChanged && !syncFromChanged) return [];
      if (!this.isSupportedFrequency(item.frequency)) return [];
      return [
        {
          dataModel: item.dataModel,
          frequency: item.frequency,
          interval: item.interval,
          ...(syncFromChanged ? { syncFrom: item.syncFrom } : {}),
        },
      ];
    });
  }

  /** True when any model now starts earlier than before, i.e. older data exists that has not been pulled. */
  static movesSyncFromEarlier(
    saved: IntegrationDataModelSyncSettings[],
    draft: IntegrationDataModelSyncSettings[]
  ): boolean {
    return draft.some((item) => {
      const current = saved.find((s) => s.dataModel === item.dataModel);
      if (!current || current.syncFrom === item.syncFrom) return false;
      if (item.syncFrom === null) return current.syncFrom !== null;
      return current.syncFrom !== null && item.syncFrom < current.syncFrom;
    });
  }
}
